import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, patch, post, qs } from '../api';
import { arrow, locale, msg, tr } from '../i18n';
import { useSession, useTenant } from '../session';
import { computerStatus, phoneStatus } from '../status';
import {
  ago, Badge, bytes, Button, Card, ConfirmDialog, CopyBox, Empty, ErrorBox, Field, friendlyError, HelpTip, Loading, Modal, PageHead, RowLink, Segmented,
  StateBadge, Tabs, TechDetails, useToast, vdisplay, when,
} from '../ui';
import { useAssistant } from '../assistant/panel';
import { PlanLines } from './plan-form';
import { ChangePlanDialog, usePlans } from './plans';
import { RecoveryPointBrowser } from './restores';
import { PhoneDetail } from './phone';
import { ArchivesSection, useArchives } from './archives';

interface DeviceRow {
  id: string;
  display_name: string;
  hostname: string;
  os: string;
  os_version: string;
  status: string;
  created_at: string;
  last_success_at: string | null;
  last_seen_at: string | null;
  last_job_state: string | null;
  storage_logical_bytes: number;
  policy_id: string | null;
  policy_name: string | null;
  is_default_policy: boolean;
  protection: { online: boolean; protected: boolean; reason: string };
  kind: 'computer' | 'phone';
  owner_name: string | null;
}

const OS_NAMES: Record<string, string> = { windows: 'Windows', linux: 'Linux', android: 'Android', ios: 'iPhone' };
const osName = (os: string) => OS_NAMES[os] ?? os;

/**
 * "Windows 11 Pro", "Ubuntu 24.04", "Android 15". Phones paired before the app reported its version sent
 * the Linux fallback (Android builds as Linux), so for a phone only an answer that names its own system
 * is trusted.
 */
export function systemName(d: { os: string; os_version?: string | null; kind?: string }): string {
  const v = d.os_version ?? '';
  if (d.kind === 'phone') return /^(android|ios|ipados)/i.test(v) ? v : osName(d.os);
  return v || osName(d.os);
}

// ───────── list ─────────

export function Devices() {
  const t = useTenant();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [view, setView] = useState<'all' | 'attention' | 'removed'>('all');
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [changing, setChanging] = useState(false);
  const list = useQuery({
    queryKey: ['devices', t.tenantId, q, view === 'removed'],
    queryFn: () => get<{ devices: DeviceRow[] }>(`/api/t/${t.tenantId}/devices${qs({ q, status: view === 'removed' ? 'retired' : 'all', limit: 200 })}`),
    refetchInterval: 20_000,
  });
  const manage = t.can('devices.manage');
  const shown = (list.data?.devices ?? []).filter((d) => {
    if (view === 'removed') return true;
    if (d.status === 'retired') return false;
    const st = d.kind === 'phone' ? phoneStatus(d) : computerStatus(d);
    return view === 'all' || st.needsAttention || d.status === 'quarantined';
  });
  // Phones are listed apart: they have an owner instead of a backup plan, and nothing to select for one.
  const devices = shown.filter((d) => d.kind !== 'phone');
  const phones = shown.filter((d) => d.kind === 'phone');
  const toggle = (id: string) => setSel((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    return n;
  });
  useEffect(() => setSel(new Set()), [view, q]);
  return (
    <div>
      <PageHead
        title={tr('Computers and phones')}
        desc={tr('Computers are backed up automatically, following their backup plan. Phones save files as soon as they are added in the app.')}
        actions={
          <div className="row">
            {/* Connecting your own phone is not an administrator's job: it is one scan, for yourself. */}
            <Link className="btn" to={`${t.base}/devices/add-phone`}>{tr('Connect a phone')}</Link>
            {manage && <Link className="btn primary" to={`${t.base}/devices/add`}>{tr('Connect a computer')}</Link>}
          </div>
        }
      />
      <div className="row" style={{ marginBottom: 14 }}>
        <input type="search" placeholder={tr('Search by name…')} value={q} onChange={(e) => setQ(e.target.value)} style={{ maxWidth: 280 }} aria-label={tr('Search computers')} />
        <Segmented value={view} onChange={setView} options={[{ key: 'all', label: tr('All') }, { key: 'attention', label: tr('Needs attention') }, { key: 'removed', label: tr('Removed') }]} />
        {sel.size > 0 && t.can('policies.manage') && <Button onClick={() => setChanging(true)}>{tr('Change backup plan ({n})', { n: sel.size })}</Button>}
      </div>
      <div className="card">
        {list.isLoading ? <div className="card-pad"><Loading /></div> : list.error ? <div className="card-pad"><ErrorBox error={list.error} /></div> : devices.length === 0 ? (
          <Empty glyph={view === 'attention' ? '✓' : '⌂'} title={q ? tr('No computers match') : view === 'attention' ? tr('Nothing needs attention') : view === 'removed' ? tr('No removed computers') : tr('No computers yet')}>
            {manage && !q && view === 'all' && <Link className="btn primary" to={`${t.base}/devices/add`} style={{ marginTop: 12 }}>{tr('Connect your first computer')}</Link>}
          </Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  {manage && view !== 'removed' && <th style={{ width: 36 }}><input type="checkbox" aria-label={tr('Select all')} checked={sel.size === devices.length} onChange={(e) => setSel(e.target.checked ? new Set(devices.map((d) => d.id)) : new Set())} /></th>}
                  <th>{tr('Computer')}</th><th>{tr('Status')}</th><th>{tr('Backup plan')}</th><th className="right">{tr('Size of files')}</th>
                </tr>
              </thead>
              <tbody>
                {devices.map((d) => {
                  const st = computerStatus(d);
                  return (
                    <tr key={d.id} className="clickable" onClick={() => nav(`${t.base}/devices/${d.id}`)}>
                      {manage && view !== 'removed' && <td onClick={(e) => e.stopPropagation()}><input type="checkbox" aria-label={tr('Select {name}', { name: d.display_name })} checked={sel.has(d.id)} onChange={() => toggle(d.id)} /></td>}
                      <td>
                        <div><RowLink to={`${t.base}/devices/${d.id}`}>{d.display_name}</RowLink></div>
                        <div className="small muted">{systemName(d)}</div>
                      </td>
                      <td><Badge tone={st.tone}>{st.label}</Badge><div className="small muted" style={{ marginTop: 3 }}>{st.seen}</div></td>
                      <td className="small">{d.policy_name ? <>{d.policy_name} {d.is_default_policy && <span className="chip">{tr('Default')}</span>}</> : <span className="muted">{tr('No backup plan')}</span>}</td>
                      <td className="right num small">{d.storage_logical_bytes ? bytes(d.storage_logical_bytes) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {!list.isLoading && phones.length > 0 && (
        <div style={{ marginTop: 20 }}>
        <Card title={tr('Phones')} pad={false}>
          <div className="table-wrap">
            <table>
              <thead><tr><th>{tr('Phone')}</th><th>{tr('Whose phone')}</th><th>{tr('Status')}</th><th className="right">{tr('Size of files')}</th></tr></thead>
              <tbody>
                {phones.map((d) => {
                  const st = phoneStatus(d);
                  return (
                    <tr key={d.id} className="clickable" onClick={() => nav(`${t.base}/devices/${d.id}`)}>
                      <td>
                        <div><RowLink to={`${t.base}/devices/${d.id}`}>{d.display_name}</RowLink></div>
                        <div className="small muted">{systemName(d)}</div>
                      </td>
                      <td className="small">{d.owner_name ?? '—'}</td>
                      <td><Badge tone={st.tone}>{st.label}</Badge><div className="small muted" style={{ marginTop: 3 }}>{st.seen}</div></td>
                      <td className="right num small">{d.storage_logical_bytes ? bytes(d.storage_logical_bytes) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
        </div>
      )}
      {changing && <ChangePlanDialog deviceIds={[...sel]} onClose={() => setChanging(false)} onDone={() => { setChanging(false); setSel(new Set()); qc.invalidateQueries({ queryKey: ['devices'] }); }} />}
    </div>
  );
}

// ───────── connect a computer ─────────

interface Installer { name: string; platform: string; kind: string; arch: string; version: string; sha256: string; size: number; signed: boolean }

const INSTALLER_LABEL: Record<string, string> = {
  msi: msg('Windows installer'),
  deb: msg('Ubuntu or Debian (.deb)'),
  rpm: msg('Red Hat, Fedora or Rocky (.rpm)'),
  binary: msg('Program file only (for IT staff)'),
  'tar.gz': msg('Archive (for IT staff)'),
};

function detectOs(): 'windows' | 'linux' {
  return /Linux|X11/.test(navigator.userAgent) && !/Android/.test(navigator.userAgent) ? 'linux' : 'windows';
}

function compareVersions(a: string, b: string): number {
  const p = (v: string) => v.replace(/^v/, '').split('-')[0]!.split('.').map((x) => Number(x) || 0);
  const [x, y] = [p(a), p(b)];
  for (let i = 0; i < 3; i++) if ((x[i] ?? 0) !== (y[i] ?? 0)) return (x[i] ?? 0) < (y[i] ?? 0) ? -1 : 1;
  return 0;
}

/** One entry per kind+arch: the release pipeline can leave several versions copied into the releases volume. */
function latestOfEachKind(items: Installer[]): Installer[] {
  const latest = new Map<string, Installer>();
  for (const i of items) {
    const key = `${i.kind}:${i.arch}`;
    const cur = latest.get(key);
    if (!cur || compareVersions(i.version, cur.version) > 0) latest.set(key, i);
  }
  return [...latest.values()];
}

export function AddComputer() {
  const t = useTenant();
  const { me } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const downloads = useQuery({ queryKey: ['downloads'], queryFn: () => get<{ installers: Installer[] }>('/api/downloads') });
  const plans = usePlans();
  const ten = useQuery({ queryKey: ['tenant', t.tenantId], queryFn: () => get(`/api/t/${t.tenantId}`) });
  const dash = useQuery({ queryKey: ['dashboard', t.tenantId], queryFn: () => get(`/api/t/${t.tenantId}/dashboard`) });
  const [os, setOs] = useState<'windows' | 'linux'>(detectOs);
  const [planId, setPlanId] = useState('');
  const [code, setCode] = useState<{ token: string; serverUrl: string; expiresAt: string; createdAt: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [waited, setWaited] = useState(0);
  // Step 1 folds away once the installer is downloaded, so the page leads on to the code.
  const [downloaded, setDownloaded] = useState(false);
  const [dlOpen, setDlOpen] = useState(true);
  const ready = (plans.data?.policies ?? []).filter((p) => p.complete);
  const defaultPlan = ready.find((p) => p.is_default);
  const chosenPlan = ready.find((p) => p.id === (planId || defaultPlan?.id));
  const installers = downloads.data?.installers ?? [];
  const mine = latestOfEachKind(installers.filter((i) => i.platform === os && (os === 'windows' ? i.kind === 'msi' : i.kind === 'deb' || i.kind === 'rpm')));
  const rest = installers.filter((i) => !mine.includes(i));
  const limit = ten.data?.tenant?.device_limit as number | undefined;
  const full = limit !== undefined && dash.data && dash.data.devices.total >= limit;
  const unverified = !me?.user.emailVerified && t.support === null;
  // After a code is created, watch for the computer that uses it.
  const watch = useQuery({
    queryKey: ['devices-new', t.tenantId],
    queryFn: () => get<{ devices: DeviceRow[] }>(`/api/t/${t.tenantId}/devices?limit=200`),
    enabled: !!code,
    refetchInterval: code ? 4000 : false,
  });
  const newest = code ? (watch.data?.devices ?? []).filter((d) => Date.parse(d.created_at) >= code.createdAt - 60_000).sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))[0] : undefined;
  useEffect(() => {
    if (!code || newest) return;
    const h = setInterval(() => setWaited(Date.now() - code.createdAt), 5000);
    return () => clearInterval(h);
  }, [code, newest]);
  const create = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await post(`/api/t/${t.tenantId}/enrollment-tokens`, { kind: 'single', expiresInMinutes: 24 * 60, policyId: chosenPlan?.id ?? null });
      setCode({ token: r.token, serverUrl: r.serverUrl ?? window.location.origin, expiresAt: r.expiresAt, createdAt: Date.now() });
      setWaited(0);
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };
  const download = (i: Installer) => (
    <a key={i.name} className="btn primary" href={`/api/downloads/${encodeURIComponent(i.name)}`} onClick={() => { setDownloaded(true); setDlOpen(false); }}>
      {os === 'windows' ? tr('Download ZeroGap for Windows') : tr('Download: {kind}', { kind: tr(INSTALLER_LABEL[i.kind] ?? i.kind) })}
    </a>
  );
  return (
    <div>
      <PageHead eyebrow={<Link to={`${t.base}/devices`}>{arrow.back()} {tr('Computers')}</Link>} title={tr('Connect a computer')} desc={tr('Do this on the computer you want to back up. It takes about five minutes.')} />
      {unverified && (
        <div className="notice warn" style={{ marginBottom: 16 }}>
          <strong>{tr('Please confirm your email address first')}</strong>
          {tr('We sent you an email with a link. After you click it, come back here.')}
          <div style={{ marginTop: 8 }}><Button size="sm" onClick={async () => { await post('/api/auth/verify-email/resend'); toast(tr('We sent the email again.')); }}>{tr('Resend the email')}</Button></div>
        </div>
      )}
      {full && (
        <div className="notice warn" style={{ marginBottom: 16 }}>
          <strong>{tr('Your subscription is full')}</strong>
          {tr('It allows {n} computers. Remove a computer you no longer use, or upgrade the subscription.', { n: limit! })}
          <div style={{ marginTop: 8 }}><Link className="btn sm" to={`${t.base}/billing`}>{tr('See subscription')}</Link></div>
        </div>
      )}
      <div className="stack step-card">
        <Card
          title={tr('1. Download ZeroGap')}
          collapsed={!dlOpen}
          onToggle={() => setDlOpen(!dlOpen)}
          actions={downloaded && !dlOpen ? <Badge tone="ok">{tr('Downloaded')}</Badge> : undefined}
        >
          {installers.length === 0 ? (
            <div className="notice warn" style={{ margin: 0 }}>{tr('The installer is not available on this server yet. Please contact support.')}</div>
          ) : (
            <>
              <p style={{ marginTop: 0 }}>{os === 'windows' ? tr('Download the installer on the computer you want to back up, then open it.') : tr('Download the package that fits the computer, then install it.')}</p>
              <div className="row">{mine.map(download)}</div>
              <p className="small" style={{ marginBottom: 0 }}>
                <a href="#" onClick={(e) => { e.preventDefault(); setOs(os === 'windows' ? 'linux' : 'windows'); }}>{os === 'windows' ? tr('Is it a Linux computer?') : tr('Is it a Windows computer?')}</a>
              </p>
              <TechDetails>
                <table>
                  <tbody>
                    {[...mine, ...rest].sort((a, b) => compareVersions(b.version, a.version)).map((i) => (
                      <tr key={i.name}>
                        <td className="small"><a href={`/api/downloads/${encodeURIComponent(i.name)}`}>{i.name}</a><div className="mono muted" dir="ltr">sha256 {i.sha256}</div></td>
                        <td className="small">{tr(INSTALLER_LABEL[i.kind] ?? i.kind)} · {i.arch} · v{i.version}</td>
                        <td>{i.signed && <Badge tone="ok">{tr('Signed')}</Badge>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TechDetails>
            </>
          )}
        </Card>

        <Card title={tr('2. Enter the connection code')}>
          {!code ? (
            <>
              <p style={{ marginTop: 0 }}>{tr('The installer asks for a connection code. Create it here.')}</p>
              {ready.length > 0 && (
                <Field label={<>{tr('Backup plan for this computer')} <HelpTip>{tr('What gets backed up and when. You can change it later on the computer’s page.')}</HelpTip></>}>
                  <select value={chosenPlan?.id ?? ''} onChange={(e) => setPlanId(e.target.value)} style={{ maxWidth: 360 }}>
                    {ready.map((p) => <option key={p.id} value={p.id}>{p.name}{p.is_default ? ` (${tr('default')})` : ''}</option>)}
                  </select>
                </Field>
              )}
              {chosenPlan && <div style={{ marginBottom: 14 }}><PlanLines spec={chosenPlan.spec} /></div>}
              {err && <div className="notice bad" style={{ marginBottom: 12 }}>{err}</div>}
              <Button variant="primary" busy={busy} disabled={unverified || !!full || !t.can('devices.manage')} onClick={create}>{tr('Create a connection code')}</Button>
            </>
          ) : (
            <div className="stack">
              {os === 'linux' ? (
                <>
                  <p style={{ margin: 0 }}>{tr('When the installer asks, copy these two lines into it:')}</p>
                  <CopyBox label={tr('Server address')} text={code.serverUrl} />
                  <CopyBox label={tr('Connection code')} text={code.token} />
                </>
              ) : (
                <>
                  <p style={{ margin: 0 }}>{tr('When the installer asks, copy this code into it:')}</p>
                  <CopyBox label={tr('Connection code')} text={code.token} />
                </>
              )}
              <p className="small muted" style={{ margin: 0 }}>{tr('The code works for one computer and expires {time}. Keep it private.', { time: when(code.expiresAt) })}</p>
              {os === 'windows' && <TechDetails inline><CopyBox label={tr('Server address')} text={code.serverUrl} /></TechDetails>}
            </div>
          )}
        </Card>

        {code && (
          <Card title={tr('3. Waiting for the computer')}>
            {!newest ? (
              <>
                <div className="row"><span className="spin" /> <span>{tr('Waiting for the computer to connect… This page updates by itself.')}</span></div>
                {waited > 3 * 60_000 && (
                  <div className="notice" style={{ marginTop: 12 }}>
                    <strong>{tr('Taking longer than usual?')}</strong>
                    <ul style={{ margin: '6px 0 0', paddingInlineStart: 18 }}>
                      <li>{tr('Check that the computer is connected to the internet.')}</li>
                      <li>{os === 'linux' ? tr('Check that the server address and code were copied exactly, with nothing missing.') : tr('Check that the code was copied exactly, with nothing missing.')}</li>
                      <li>{tr('If the installer finished without asking for the code, create a new code and run the installer again.')}</li>
                    </ul>
                  </div>
                )}
              </>
            ) : newest.status === 'pending' ? (
              <div className="row"><span className="spin" /> <span>{tr('{name} is connected. Getting it ready…', { name: newest.display_name })}</span></div>
            ) : (
              <div className="notice ok" style={{ margin: 0 }}>
                <strong>{tr('{name} is connected and protected by “{plan}”.', { name: newest.display_name, plan: newest.policy_name ?? '' })}</strong>
                {tr('Its first backup starts on its own. You can also start it right away.')}
                <div className="row" style={{ marginTop: 10 }}>
                  {t.can('jobs.manage') && (
                    <Button variant="primary" size="sm" onClick={async () => {
                      try {
                        await post(`/api/t/${t.tenantId}/devices/${newest.id}/backup`);
                        toast(tr('Backup requested. The computer starts within a minute.'));
                        qc.invalidateQueries({ queryKey: ['devices'] });
                      } catch (e) {
                        toast(friendlyError(e), true);
                      }
                    }}>{tr('Start the first backup now')}</Button>
                  )}
                  <Link className="btn sm" to={`${t.base}/devices/${newest.id}`}>{tr('Open the computer')}</Link>
                  <Button size="sm" variant="ghost" onClick={() => setCode(null)}>{tr('Connect another computer')}</Button>
                </div>
              </div>
            )}
          </Card>
        )}

        <ManyComputers installers={installers} planId={chosenPlan?.id ?? null} disabled={unverified || !!full} />
      </div>
    </div>
  );
}

/** For IT staff: one code for many computers, and a silent install command. */
function ManyComputers({ installers, planId, disabled }: { installers: Installer[]; planId: string | null; disabled: boolean }) {
  const t = useTenant();
  const [count, setCount] = useState(10);
  const [hours, setHours] = useState(24);
  const [code, setCode] = useState<{ token: string; serverUrl: string; expiresAt: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const msi = installers.find((i) => i.kind === 'msi')?.name ?? 'ZeroGapAgent.msi';
  return (
    <TechDetails summary={tr('Connecting many computers at once? (for IT staff)')}>
      <p className="small" style={{ marginTop: 0 }}>{tr('Create one code that several computers can use, and install without questions, for example with Group Policy or a remote management tool.')}</p>
      {!code ? (
        <>
          <div className="grid g2">
            <Field label={tr('How many computers')}><input type="number" min={2} max={500} value={count} onChange={(e) => setCount(Number(e.target.value))} /></Field>
            <Field label={tr('Code expires after (hours, up to 72)')}><input type="number" min={1} max={72} value={hours} onChange={(e) => setHours(Number(e.target.value))} /></Field>
          </div>
          {err && <div className="notice bad" style={{ marginBottom: 10 }}>{err}</div>}
          <Button disabled={disabled} onClick={async () => {
            setErr(null);
            try {
              const r = await post(`/api/t/${t.tenantId}/enrollment-tokens`, { kind: 'bulk', maxUses: count, expiresInMinutes: Math.min(72, hours) * 60, policyId: planId });
              setCode({ token: r.token, serverUrl: r.serverUrl ?? window.location.origin, expiresAt: r.expiresAt });
            } catch (e) {
              setErr(friendlyError(e));
            }
          }}>{tr('Create a code for {n} computers', { n: count })}</Button>
        </>
      ) : (
        <div className="stack">
          <CopyBox label={tr('Connection code')} text={code.token} />
          <CopyBox label={tr('Windows, silent install')} text={`msiexec /i "${msi}" /qn SERVER_URL="${code.serverUrl}" ENROLLMENT_TOKEN="${code.token}"`} />
          <CopyBox label={tr('Linux, after installing the package')} text={`sudo zerogap-agent enroll --server "${code.serverUrl}" --token "${code.token}"`} />
          <p className="small muted" style={{ margin: 0 }}>{tr('Expires {time}.', { time: when(code.expiresAt) })}</p>
        </div>
      )}
    </TechDetails>
  );
}

// ───────── one computer ─────────

export function DeviceDetail() {
  const t = useTenant();
  const { deviceId } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const plans = usePlans();
  const [tab, setTab] = useState<'backups' | 'history' | 'settings' | 'tech'>('backups');
  const [changing, setChanging] = useState(false);
  const [skipped, setSkipped] = useState<string | null>(null);
  const assistant = useAssistant();
  const d = useQuery({ queryKey: ['device', deviceId], queryFn: () => get(`/api/t/${t.tenantId}/devices/${deviceId}`), refetchInterval: 10_000 });
  const archives = useArchives(deviceId, d.data?.device?.kind === 'computer');
  const refresh = () => qc.invalidateQueries({ queryKey: ['device', deviceId] });
  if (d.isLoading) return <Loading rows={8} />;
  if (d.error) return <ErrorBox error={d.error} />;
  const dev = d.data.device;
  if (dev.kind === 'phone') return <PhoneDetail data={d.data} onChanged={refresh} />;
  const ep = d.data.effectivePolicy;
  const job = d.data.activeJob;
  // An archive upload holds the computer's one job slot too, but it is not a backup.
  const archiving = job?.kind === 'archive';
  const st = computerStatus(dev, !!job && !archiving);
  const hasArchived = (archives.data?.archives ?? []).some((a) => a.cloud.files > 0);
  const plan = plans.data?.policies.find((p) => p.id === ep?.id);
  const lrp = d.data.lastRecoveryPoint;
  return (
    <div>
      <PageHead
        eyebrow={<Link to={`${t.base}/devices`}>{arrow.back()} {tr('Computers')}</Link>}
        title={dev.display_name}
        desc={<>{systemName(dev)} · {st.seen}</>}
        actions={
          <>
            {t.can('jobs.manage') && dev.status === 'active' && (
              <Button variant="primary" disabled={!!job} onClick={async () => {
                try {
                  await post(`/api/t/${t.tenantId}/devices/${deviceId}/backup`);
                  toast(tr('Backup requested. The computer starts within a minute.'));
                  refresh();
                } catch (e) {
                  toast(friendlyError(e), true);
                }
              }}>{job && !archiving ? tr('Backing up now…') : tr('Back up now')}</Button>
            )}
            {t.can('restores.create') && (dev.last_success_rp_id || hasArchived) && <Link className="btn" to={`${t.base}/restore?device=${deviceId}`}>{tr('Get files back')}</Link>}
            {assistant.available && <Button variant="ghost" onClick={() => assistant.open(deviceId)}>{tr('Ask about this computer')}</Button>}
          </>
        }
      />
      <div className="grid g2">
        <Card title={tr('Backup status')}>
          <div style={{ marginBottom: 12 }}>
            <Badge tone={st.tone}>{st.label}</Badge>
            {st.hint && <div className="small muted" style={{ marginTop: 6 }}>{st.hint}</div>}
          </div>
          {job && archiving && (
            <div className="notice" style={{ marginBottom: 12 }}>
              <strong>{tr('Moving old files to the archive…')}</strong>
              <span>{tr('{files} files checked · {uploaded} sent', { files: (job.progress?.filesScanned ?? 0).toLocaleString(locale()), uploaded: bytes(job.progress?.bytesUploaded) })}</span>
            </div>
          )}
          {job && !archiving && (
            <div className="notice" style={{ marginBottom: 12 }}>
              <strong>{tr('Backing up now…')}</strong>
              <span>{tr('{files} files checked · {uploaded} sent', { files: (job.progress?.filesScanned ?? 0).toLocaleString(locale()), uploaded: bytes(job.progress?.bytesUploaded) })}</span>
              {t.can('jobs.manage') && <div style={{ marginTop: 8 }}><Button size="sm" variant="danger" onClick={async () => { await post(`/api/t/${t.tenantId}/jobs/${job.id}/cancel`); toast(tr('The backup will stop in a moment')); refresh(); }}>{tr('Stop this backup')}</Button></div>}
            </div>
          )}
          <dl className="kv">
            <dt>{tr('Last backup')}</dt><dd>{lrp ? <>{when(lrp.finished_at)} <span className="muted small">({ago(lrp.finished_at)})</span><div className="small muted">{tr('{files} files · {size}', { files: Number(lrp.file_count).toLocaleString(locale()), size: bytes(lrp.total_bytes) })}</div>
              {lrp.status === 'partial' && lrp.error_count > 0 && (
                <div className="small" style={{ marginTop: 4 }}>
                  <span style={{ color: 'var(--warn)' }}>{tr("{n} files couldn't be saved", { n: Number(lrp.error_count).toLocaleString(locale()) })}</span>{' '}
                  <Button size="sm" variant="ghost" onClick={() => setSkipped(lrp.id)}>{tr('See which')}</Button>
                </div>
              )}</> : tr('Not yet')}</dd>
            <dt>{tr('Next backup')}</dt><dd>{d.data.nextBackupAt ? when(d.data.nextBackupAt) : ep?.paused ? tr('Paused') : '—'}</dd>
            <dt>{tr('Size of backed-up files')}</dt><dd className="num">{bytes(dev.storage_logical_bytes)}</dd>
            <dt>{tr('Saved backups')}</dt><dd className="num">{dev.recovery_point_count}</dd>
          </dl>
        </Card>
        <Card title={tr('Backup plan')} actions={t.can('policies.manage') && dev.status !== 'retired' ? <Button size="sm" onClick={() => setChanging(true)}>{tr('Change plan')}</Button> : undefined}>
          {ep ? (
            <>
              <div style={{ marginBottom: 10 }}><Link to={`${t.base}/plans/${ep.id}`}><strong>{ep.name}</strong></Link> {plan?.is_default && <span className="chip">{tr('Default')}</span>} {ep.paused && <Badge tone="warn">{tr('Paused')}</Badge>}</div>
              {plan && <PlanLines spec={plan.spec} />}
              {!d.data.policyInSync && dev.status === 'active' && <p className="small muted" style={{ marginBottom: 0 }}>{tr('The computer picks up plan changes the next time it connects.')}</p>}
            </>
          ) : (
            <p className="muted" style={{ margin: 0 }}>{tr('This computer has no backup plan, so it is not being backed up.')}</p>
          )}
          <div className="card-section">
            <ArchivesSection deviceId={dev.id} active={dev.status === 'active'} />
          </div>
        </Card>
      </div>
      {dev.status === 'quarantined' && <div className="notice bad" style={{ marginTop: 16 }}><strong>{tr('This computer is blocked')}</strong>{tr('It was reported lost or stolen {time}. It can no longer reach its backups, but you can still restore them to another computer.', { time: ago(dev.quarantined_at) })}</div>}
      <div style={{ marginTop: 20 }}>
        <Tabs value={tab} onChange={setTab} tabs={[{ key: 'backups', label: tr('Saved backups') }, { key: 'history', label: tr('History') }, { key: 'settings', label: tr('Settings') }, { key: 'tech', label: tr('Technical details') }]} />
      </div>
      {tab === 'backups' && <DevicePoints deviceId={deviceId!} pathStyle={dev.os === 'windows' ? 'windows' : 'posix'} />}
      {tab === 'history' && <DeviceJobs deviceId={deviceId!} />}
      {tab === 'settings' && <DeviceSettings dev={dev} onChanged={refresh} />}
      {tab === 'tech' && <DeviceTech dev={dev} data={d.data} onChanged={refresh} />}
      {changing && <ChangePlanDialog deviceIds={[dev.id]} currentPlanId={ep?.id ?? null} onClose={() => setChanging(false)} onDone={() => { setChanging(false); refresh(); }} />}
      {skipped && <SkippedFiles rpId={skipped} onClose={() => setSkipped(null)} />}
    </div>
  );
}

function DeviceSettings({ dev, onChanged }: { dev: any; onChanged: () => void }) {
  const t = useTenant();
  const toast = useToast();
  const [name, setName] = useState(dev.display_name);
  const [dialog, setDialog] = useState<null | 'block' | 'remove'>(null);
  const [reason, setReason] = useState('');
  const manage = t.can('devices.manage');
  return (
    <div className="grid g2">
      <Card title={tr('Name')}>
        <fieldset disabled={!manage} className="plain-fieldset">
          <Field label={tr('Name shown in ZeroGap')}><input type="text" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} /></Field>
          <Button disabled={!name.trim() || name === dev.display_name} onClick={async () => {
            try {
              await patch(`/api/t/${t.tenantId}/devices/${dev.id}`, { displayName: name.trim() });
              toast(tr('Saved'));
              onChanged();
            } catch (e) {
              toast(friendlyError(e), true);
            }
          }}>{tr('Save')}</Button>
        </fieldset>
      </Card>
      <Card title={tr('Lost, stolen or no longer used')}>
        <p className="small muted" style={{ marginTop: 0 }}>{tr('Its backups are always kept, and you can still restore them to another computer.')}</p>
        <div className="stack" style={{ gap: 10, alignItems: 'flex-start' }}>
          {t.can('devices.credentials') && !['retired', 'quarantined'].includes(dev.status) && <Button variant="danger" onClick={() => setDialog('block')}>{tr('Lost or stolen? Block this computer…')}</Button>}
          {manage && dev.status !== 'retired' && <Button onClick={() => setDialog('remove')}>{tr('Remove this computer…')}</Button>}
          {dev.status === 'retired' && <p className="small" style={{ margin: 0 }}>{tr('This computer was removed {time}.', { time: ago(dev.retired_at) })}</p>}
        </div>
      </Card>
      {dialog === 'block' && (
        <ConfirmDialog title={tr('Block {name}?', { name: dev.display_name })} confirm={tr('Block the computer')} danger onClose={() => setDialog(null)}
          onConfirm={async () => {
            await post(`/api/t/${t.tenantId}/devices/${dev.id}/quarantine`, { reason: reason.trim().length >= 3 ? reason.trim() : tr('Reported lost or stolen') });
            setDialog(null);
            onChanged();
            toast(tr('Computer blocked'));
          }}>
          <p style={{ margin: 0 }}>{tr('Use this if the computer was lost or stolen. It immediately loses all access to its backups and stops backing up. Its backups stay safe and can be restored to another computer.')}</p>
          <Field label={tr('What happened? (optional)')}><input type="text" value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} /></Field>
        </ConfirmDialog>
      )}
      {dialog === 'remove' && (
        <ConfirmDialog title={tr('Remove {name}?', { name: dev.display_name })} confirm={tr('Remove the computer')} onClose={() => setDialog(null)}
          onConfirm={async () => {
            await post(`/api/t/${t.tenantId}/devices/${dev.id}/retire`, { reason: '' });
            setDialog(null);
            onChanged();
            toast(tr('Computer removed'));
          }}>
          <p style={{ margin: 0 }}>{tr('Use this when a computer is replaced or no longer used. It stops backing up and frees its place in your subscription. Its backups are kept until they expire under its backup plan.')}</p>
        </ConfirmDialog>
      )}
    </div>
  );
}

function DeviceTech({ dev, data, onChanged }: { dev: any; data: any; onChanged: () => void }) {
  const t = useTenant();
  const toast = useToast();
  return (
    <div className="stack">
      <div className="grid g2">
        <Card title={tr('Computer')}>
          <dl className="kv">
            <dt>{tr('Computer name on the network')}</dt><dd className="mono">{dev.hostname}</dd>
            <dt>{tr('System')}</dt><dd>{systemName(dev)} {dev.arch}</dd>
            <dt>{tr('ZeroGap app version')}</dt><dd className="mono">{dev.agent_version || '—'}</dd>
            <dt>{tr('Connected since')}</dt><dd>{when(dev.enrolled_at)}</dd>
            <dt>{tr('Time zone')}</dt><dd>{dev.timezone ?? tr('unknown')}</dd>
            <dt>{tr('Plan version in use')}</dt><dd>{data.policyInSync ? tr('Up to date') : tr('Waiting for the computer')}</dd>
            <dt>{tr('Device ID')}</dt><dd className="mono small">{dev.id}</dd>
          </dl>
        </Card>
        <Card title={tr('Storage access')}>
          <p className="muted small" style={{ marginTop: 0 }}>{tr("Each computer has its own key that can reach only its own backups, can't delete history, and is never shown in full.")}</p>
          <table>
            <thead><tr><th>{tr('Key')}</th><th>{tr('State')}</th><th>{tr('Issued')}</th></tr></thead>
            <tbody>{data.credentials.map((c: any) => <tr key={c.access_key_id + c.generation}><td className="mono">{c.access_key_id}</td><td><StateBadge state={c.state} /></td><td className="small">{when(c.created_at)}</td></tr>)}</tbody>
          </table>
          {t.can('devices.credentials') && dev.status === 'active' && (
            <Button size="sm" style={{ marginTop: 12 }} onClick={async () => { await post(`/api/t/${t.tenantId}/devices/${dev.id}/credentials/rotate`); toast(tr('A new key is being issued. The old one stops working once the computer confirms.')); onChanged(); }}>{tr('Replace the key')}</Button>
          )}
        </Card>
      </div>
      <Card title={tr('Event log')} pad={false}><DeviceEvents deviceId={dev.id} /></Card>
      {dev.health && Object.keys(dev.health).length > 0 && <TechDetails summary={tr('Last report from the computer')}><pre className="codebox">{JSON.stringify(dev.health, null, 2)}</pre></TechDetails>}
    </div>
  );
}

function DevicePoints({ deviceId, pathStyle }: { deviceId: string; pathStyle: 'windows' | 'posix' }) {
  const t = useTenant();
  const [rp, setRp] = useState<string | null>(null);
  const [skipped, setSkipped] = useState<string | null>(null);
  const [releasing, setReleasing] = useState<string | null>(null);
  const qc = useQueryClient();
  const rps = useQuery({ queryKey: ['rps', deviceId], queryFn: () => get(`/api/t/${t.tenantId}/devices/${deviceId}/recovery-points?limit=100`) });
  if (rps.isLoading) return <Loading />;
  if (rps.error) return <ErrorBox error={rps.error} />;
  const list = rps.data.recoveryPoints;
  if (!list.length) return <Card><Empty glyph="◷" title={tr('No saved backups yet')}>{tr('They appear here after the first backup finishes.')}</Empty></Card>;
  return (
    <div className="grid" style={{ gridTemplateColumns: 'minmax(240px, 320px) 1fr' }}>
      <div className="card" style={{ maxHeight: 620, overflow: 'auto' }}>
        <table>
          <tbody>
            {list.map((p: any) => (
              <tr key={p.id} className="clickable" onClick={() => setRp(p.id)} style={{ background: rp === p.id ? 'var(--brass-soft)' : undefined }}>
                <td>
                  <div className="row between"><button type="button" className="row-pick" aria-pressed={rp === p.id} onClick={(e) => { e.stopPropagation(); setRp(p.id); }}>{when(p.finished_at)}</button>{['sample_verified', 'restore_verified'].includes(p.verification_status) && <span className="small" title={tr('Checked automatically')} style={{ color: 'var(--ok)' }}>✓</span>}</div>
                  <div className="small muted">{tr('{files} files · {size}', { files: Number(p.file_count).toLocaleString(locale()), size: bytes(p.total_bytes) })}</div>
                  {/* Kept (on hold): never deleted on schedule. Set by an administrator, often through the assistant. */}
                  {p.hold && (
                    <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                      <Badge tone="brass">{tr('Kept')}</Badge>
                      {t.can('retention.manage') && <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setReleasing(p.id); }}>{tr('Stop keeping')}</Button>}
                    </div>
                  )}
                  {p.status === 'partial' && (
                    <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                      <Badge tone="warn">{tr('Some files were skipped')}</Badge>
                      {p.error_count > 0 && <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setSkipped(p.id); }}>{tr('See which')}</Button>}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div>{rp ? <RecoveryPointBrowser rpId={rp} deviceId={deviceId} pathStyle={pathStyle} /> : <Card><Empty glyph={arrow.back()} title={tr('Choose a backup to see its files')} /></Card>}</div>
      {skipped && <SkippedFiles rpId={skipped} onClose={() => setSkipped(null)} />}
      {releasing && (
        <ConfirmDialog
          title={tr('Stop keeping this backup?')}
          confirm={tr('Stop keeping')}
          onClose={() => setReleasing(null)}
          onConfirm={async () => {
            await post(`/api/t/${t.tenantId}/recovery-points/${releasing}/hold`, { hold: false });
            await qc.invalidateQueries({ queryKey: ['rps', deviceId] });
            setReleasing(null);
          }}
        >
          <p style={{ margin: 0 }}>{tr('From then on it is treated like any other saved backup, and is deleted once it is older than the backup plan keeps them.')}</p>
        </ConfirmDialog>
      )}
    </div>
  );
}

const SKIP_REASON: Record<string, [string, string]> = {
  in_use: [msg('Open in another program'), msg('A program kept it open. Many of these are system files that never need a backup. If one matters, close the program before the next backup.')],
  no_permission: [msg('Not allowed to open it'), msg('The computer did not let ZeroGap open it. These are usually system files or private files of other programs.')],
  cloud_only: [msg('Only stored online'), msg('It lives in OneDrive or a similar service and was not downloaded to this computer. It is still in that online service.')],
  gone: [msg('Deleted or moved during the backup'), msg('It disappeared while the backup was running. Nothing to do.')],
  missing_folder: [msg('A folder in the plan is not on this computer'), msg('The backup plan includes a folder this computer does not have. Nothing is lost.')],
  bad_name: [msg('The name has unusual characters'), msg('Renaming the file usually fixes this.')],
  changed: [msg('Changed while it was being saved'), msg('The next backup tries again.')],
  disk_error: [msg('The disk could not read it'), msg('The disk may be starting to fail. Worth having it checked soon.')],
  other: [msg('Could not be read'), msg('Open the details for the exact message.')],
};

/** The items one backup could not save, with the reason in plain words. */
function SkippedFiles({ rpId, onClose }: { rpId: string; onClose: () => void }) {
  const t = useTenant();
  const [reason, setReason] = useState<string | null>(null);
  const [cursor, setCursor] = useState<number | null>(null);
  const [items, setItems] = useState<any[]>([]);
  const q = useQuery({
    queryKey: ['skipped', rpId, reason, cursor],
    queryFn: () => get(`/api/t/${t.tenantId}/recovery-points/${rpId}/skipped${qs({ reason: reason ?? undefined, after: cursor ?? undefined, limit: 100 })}`),
    placeholderData: keepPreviousData,
  });
  useEffect(() => {
    if (q.data && !q.isFetching) setItems((p) => (cursor === null ? q.data.items : [...p, ...q.data.items]));
  }, [q.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const pick = (r: string | null) => { setReason(r); setCursor(null); setItems([]); };
  const d = q.data;
  const counts = Object.entries((d?.reasons ?? {}) as Record<string, number>).sort((a, b) => b[1] - a[1]);
  // What to do about it: for the chosen reason, or for the only one there is.
  const hintFor = reason ?? (counts.length === 1 ? counts[0]![0] : null);
  const hint = hintFor ? SKIP_REASON[hintFor]?.[1] : undefined;
  return (
    <Modal wide title={tr("Files this backup couldn't save")} onClose={onClose} footer={<Button onClick={onClose}>{tr('Close')}</Button>}>
      {q.error ? <ErrorBox error={q.error} /> : !d ? <Loading /> : (
        <>
          <p style={{ marginTop: 0 }}>{tr('{n} items were skipped. Everything else in this backup was saved normally.', { n: Number(d.total).toLocaleString(locale()) })}</p>
          {counts.length > 1 && (
            <div className="row" style={{ flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
              <button type="button" className={`filter-chip ${reason === null ? 'on' : ''}`} aria-pressed={reason === null} onClick={() => pick(null)}>{tr('All')} · {d.listed}</button>
              {counts.map(([r, n]) => (
                <button type="button" key={r} className={`filter-chip ${reason === r ? 'on' : ''}`} aria-pressed={reason === r} onClick={() => pick(r)}>{tr(SKIP_REASON[r]?.[0] ?? r)} · {n}</button>
              ))}
            </div>
          )}
          {hint && <div className="notice" style={{ marginBottom: 12 }}>{tr(hint)}</div>}
          <div className="table-wrap" style={{ maxHeight: 420, overflow: 'auto' }}>
            <table>
              <thead><tr><th>{tr('File or folder')}</th><th>{tr('Why it was skipped')}</th></tr></thead>
              <tbody>
                {items.map((e) => (
                  <tr key={e.id}>
                    <td className="mono small" dir="ltr" style={{ textAlign: 'start', wordBreak: 'break-all' }}><bdi>{vdisplay(e.path, d.pathStyle)}</bdi></td>
                    <td className="small">
                      {tr(SKIP_REASON[e.reason]?.[0] ?? e.reason)}
                      <TechDetails inline summary={tr('Details')}><span className="mono small" dir="ltr">{e.op}: {e.message}</span></TechDetails>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {q.isFetching && <div className="card-pad"><Loading rows={2} /></div>}
            {d.listed === 0 && <Empty glyph="∅" title={tr('The list of skipped files was not kept for this backup')} />}
          </div>
          {d.nextAfter && <div style={{ marginTop: 12 }}><Button onClick={() => setCursor(d.nextAfter)} busy={q.isFetching}>{tr('Show more')}</Button></div>}
          {d.listed < d.total && <p className="small muted" style={{ marginBottom: 0 }}>{tr('Only the first {n} are listed.', { n: Number(d.listed).toLocaleString(locale()) })}</p>}
        </>
      )}
    </Modal>
  );
}

const TRIGGER: Record<string, string> = {
  scheduled: msg('Scheduled'),
  manual: msg('Started by hand'),
  catchup: msg('Catching up after the computer was off'),
  retry: msg('Retry'),
  offline: msg('While offline'),
  change: msg('A file arrived'),
  'safety-net': msg('Routine check'),
  share: msg('Shared to ZeroGap'),
  archive: msg('Moving old files to the archive'),
};
const RESULT: Record<string, [string, 'ok' | 'warn' | 'bad' | 'info' | 'neutral']> = {
  completed: [msg('Finished'), 'ok'],
  partial: [msg('Finished, some files skipped'), 'warn'],
  failed: [msg("Didn't finish"), 'bad'],
  running: [msg('Running'), 'info'],
  committing: [msg('Finishing'), 'info'],
  queued: [msg('Waiting to start'), 'neutral'],
  retrying: [msg('Trying again'), 'warn'],
  canceled: [msg('Stopped'), 'neutral'],
  expired: [msg("Didn't start"), 'neutral'],
};

function DeviceJobs({ deviceId }: { deviceId: string }) {
  const t = useTenant();
  const [skipped, setSkipped] = useState<string | null>(null);
  const jobs = useQuery({ queryKey: ['jobs', deviceId], queryFn: () => get(`/api/t/${t.tenantId}/devices/${deviceId}/jobs`), refetchInterval: 15_000 });
  if (jobs.isLoading) return <Loading />;
  if (jobs.error) return <ErrorBox error={jobs.error} />;
  if (!jobs.data.jobs.length) return <Card><Empty glyph="◷" title={tr('No backups yet')} /></Card>;
  return (
    <div className="card table-wrap">
      <table>
        <thead><tr><th>{tr('When')}</th><th>{tr('Why')}</th><th>{tr('Result')}</th><th className="right">{tr('Files')}</th><th>{tr('Took')}</th></tr></thead>
        <tbody>
          {jobs.data.jobs.map((j: any) => {
            const [label, tone] = RESULT[j.state] ?? [j.state, 'neutral'];
            return (
              <tr key={j.id}>
                <td className="small">{when(j.started_at ?? j.created_at)}</td>
                <td className="small">{tr(TRIGGER[j.trigger] ?? j.trigger)}</td>
                <td>
                  <Badge tone={tone}>{tr(label)}</Badge>
                  {j.error && <TechDetails inline summary={tr('Why?')}><span className="mono small" dir="ltr">{j.error}</span></TechDetails>}
                  {!j.error && j.warning_count > 0 && (
                    <div className="small muted">
                      {tr('{n} files could not be read', { n: j.warning_count })}
                      {j.recovery_point_id && <> <Button size="sm" variant="ghost" onClick={() => setSkipped(j.recovery_point_id)}>{tr('See which')}</Button></>}
                    </div>
                  )}
                </td>
                <td className="right num small">{j.stats?.files?.toLocaleString(locale()) ?? '—'}</td>
                <td className="small">{j.stats?.durationMs ? durationText(j.stats.durationMs) : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {skipped && <SkippedFiles rpId={skipped} onClose={() => setSkipped(null)} />}
    </div>
  );
}

function durationText(ms: number) {
  const s = Math.round(ms / 1000);
  if (s < 60) return tr('{n} sec', { n: s });
  const m = Math.round(s / 60);
  if (m < 60) return tr('{n} min', { n: m });
  return tr('{h} h {m} min', { h: Math.floor(m / 60), m: m % 60 });
}

export function DeviceEvents({ deviceId }: { deviceId: string }) {
  const t = useTenant();
  const [skipped, setSkipped] = useState<string | null>(null);
  const ev = useQuery({ queryKey: ['events', deviceId], queryFn: () => get(`/api/t/${t.tenantId}/devices/${deviceId}/events?limit=200`), refetchInterval: 20_000 });
  const tone = useMemo(() => ({ info: 'info', warn: 'warn', error: 'bad', debug: 'neutral' }) as Record<string, 'info' | 'warn' | 'bad' | 'neutral'>, []);
  if (ev.isLoading) return <div className="card-pad"><Loading /></div>;
  if (ev.error) return <div className="card-pad"><ErrorBox error={ev.error} /></div>;
  if (!ev.data.events.length) return <Empty glyph="·" title={tr('No events')} />;
  return (
    <div className="table-wrap" style={{ maxHeight: 420, overflow: 'auto' }}>
      <table>
        <tbody>
          {ev.data.events.map((e: any) => (
            <tr key={e.id}>
              <td className="small" style={{ width: 170 }}>{when(e.occurred_at)}</td>
              <td style={{ width: 90 }}><Badge tone={tone[e.level] ?? 'neutral'}>{tr(e.level)}</Badge></td>
              <td className="small" dir="ltr" style={{ textAlign: 'start' }}>{e.message}</td>
              <td className="right" style={{ width: 1, whiteSpace: 'nowrap' }}>
                {e.kind === 'backup' && e.level === 'warn' && e.details?.recoveryPointId && <Button size="sm" variant="ghost" onClick={() => setSkipped(e.details.recoveryPointId)}>{tr('See skipped files')}</Button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {skipped && <SkippedFiles rpId={skipped} onClose={() => setSkipped(null)} />}
    </div>
  );
}

